import * as THREE from 'three';
import { Square } from '../core/types.ts';
import { TILE_SIZE, squareToCoords } from './pieceView.ts';

export class BoardView {
  private group: THREE.Group;
  private tiles: Map<Square, THREE.Mesh> = new Map();
  private tileMeshesList: THREE.Mesh[] = [];

  // Highlight objects
  private selectedHighlight: THREE.Mesh;
  private checkHighlight: THREE.Mesh;
  private lastMoveFromHighlight: THREE.Mesh;
  private lastMoveToHighlight: THREE.Mesh;

  private legalMoveHighlights: THREE.Mesh[] = [];
  private highlightPool: THREE.Mesh[] = [];

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'ChessboardGroup';

    this.createBoardTiles();
    this.createBorderFrame();
    this.createCoordinates();

    // Prepare highlight indicators
    this.selectedHighlight = this.createRingHighlight(0x00e5ff, 0.75);
    this.checkHighlight = this.createRingHighlight(0xff0033, 0.85);
    this.lastMoveFromHighlight = this.createTilePlaneHighlight(0xffcc00, 0.35);
    this.lastMoveToHighlight = this.createTilePlaneHighlight(0xffaa00, 0.45);

    this.group.add(this.selectedHighlight);
    this.group.add(this.checkHighlight);
    this.group.add(this.lastMoveFromHighlight);
    this.group.add(this.lastMoveToHighlight);

    this.hideAllHighlights();
  }

  public getGroup(): THREE.Group {
    return this.group;
  }

  public getTileMeshes(): THREE.Mesh[] {
    return this.tileMeshesList;
  }

  private createBoardTiles(): void {
    // Stone materials
    const lightTileMat = new THREE.MeshStandardMaterial({
      color: 0xddd7cc,
      roughness: 0.35,
      metalness: 0.12,
      bumpScale: 0.05
    });

    const darkTileMat = new THREE.MeshStandardMaterial({
      color: 0x242830,
      roughness: 0.45,
      metalness: 0.25,
      bumpScale: 0.08
    });

    const tileGeom = new THREE.BoxGeometry(TILE_SIZE * 0.98, 0.4, TILE_SIZE * 0.98);

    for (let rank = 1; rank <= 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const fileChar = String.fromCharCode(97 + file);
        const square = `${fileChar}${rank}` as Square;
        const isLight = (file + rank) % 2 !== 0;

        const tileMesh = new THREE.Mesh(tileGeom, isLight ? lightTileMat : darkTileMat);
        const coords = squareToCoords(square);
        tileMesh.position.set(coords.x, 0.2, coords.z);
        tileMesh.receiveShadow = true;
        tileMesh.userData = { square, isTile: true };

        this.tiles.set(square, tileMesh);
        this.tileMeshesList.push(tileMesh);
        this.group.add(tileMesh);
      }
    }
  }

  private createBorderFrame(): void {
    // Heavy carved stone border frame around the 8x8 tiles
    const frameMat = new THREE.MeshStandardMaterial({
      color: 0x16181f,
      roughness: 0.6,
      metalness: 0.35
    });

    const goldTrimMat = new THREE.MeshStandardMaterial({
      color: 0xc8963e,
      roughness: 0.3,
      metalness: 0.8,
      emissive: 0x332200,
      emissiveIntensity: 0.2
    });

    const totalWidth = TILE_SIZE * 8;
    const borderThickness = 1.4;
    const borderDepth = 0.55;

    // Sub-base slab
    const baseSlabGeom = new THREE.BoxGeometry(totalWidth + borderThickness * 2 + 0.4, 0.6, totalWidth + borderThickness * 2 + 0.4);
    const baseSlab = new THREE.Mesh(baseSlabGeom, frameMat);
    baseSlab.position.set(0, -0.1, 0);
    baseSlab.receiveShadow = true;
    this.group.add(baseSlab);

    // Gilded inlay trim around the tiles edge
    const trimGeom = new THREE.BoxGeometry(totalWidth + 0.15, 0.42, totalWidth + 0.15);
    const trimWire = new THREE.BoxGeometry(totalWidth + 0.25, 0.43, totalWidth + 0.25);
    const trimMesh = new THREE.Mesh(trimWire, goldTrimMat);
    trimMesh.position.set(0, 0.18, 0);
    this.group.add(trimMesh);
  }

  private createCoordinates(): void {
    // Generate canvas texture with carved runes / coordinates A-H and 1-8
    const files = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    const ranks = ['1', '2', '3', '4', '5', '6', '7', '8'];

    const createTextPlane = (text: string) => {
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const ctx = canvas.getContext('2d')!;

      ctx.clearRect(0, 0, 128, 128);
      ctx.fillStyle = '#dfb56c';
      ctx.font = 'bold 72px "Cinzel", "Times New Roman", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = '#ffdd88';
      ctx.shadowBlur = 12;
      ctx.fillText(text, 64, 64);

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;

      const mat = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide
      });

      const geom = new THREE.PlaneGeometry(0.8, 0.8);
      geom.rotateX(-Math.PI / 2);
      return new THREE.Mesh(geom, mat);
    };

    // Place coordinate letters on front (White) and back (Black) borders
    for (let f = 0; f < 8; f++) {
      const x = (f - 3.5) * TILE_SIZE;

      // Front border (Z = 8.6)
      const labelFront = createTextPlane(files[f]);
      labelFront.position.set(x, 0.42, 8.65);
      this.group.add(labelFront);

      // Back border (Z = -8.6)
      const labelBack = createTextPlane(files[f]);
      labelBack.position.set(x, 0.42, -8.65);
      labelBack.rotation.y = Math.PI;
      this.group.add(labelBack);
    }

    // Place numbers on left (File A) and right (File H) borders
    for (let r = 0; r < 8; r++) {
      const z = (3.5 - r) * TILE_SIZE;

      // Left border (X = -8.65)
      const numLeft = createTextPlane(ranks[r]);
      numLeft.position.set(-8.65, 0.42, z);
      this.group.add(numLeft);

      // Right border (X = 8.65)
      const numRight = createTextPlane(ranks[r]);
      numRight.position.set(8.65, 0.42, z);
      numRight.rotation.y = Math.PI;
      this.group.add(numRight);
    }
  }

  // Highlight Meshes Creation
  private createRingHighlight(colorHex: number, opacity: number): THREE.Mesh {
    const geom = new THREE.RingGeometry(0.65, 0.88, 32);
    geom.rotateX(-Math.PI / 2);

    const mat = new THREE.MeshBasicMaterial({
      color: colorHex,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.y = 0.41;
    mesh.visible = false;
    return mesh;
  }

  private createTilePlaneHighlight(colorHex: number, opacity: number): THREE.Mesh {
    const geom = new THREE.PlaneGeometry(TILE_SIZE * 0.94, TILE_SIZE * 0.94);
    geom.rotateX(-Math.PI / 2);

    const mat = new THREE.MeshBasicMaterial({
      color: colorHex,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.y = 0.405;
    mesh.visible = false;
    return mesh;
  }

  public setSelectedSquare(square: Square | null): void {
    if (!square) {
      this.selectedHighlight.visible = false;
      return;
    }
    const coords = squareToCoords(square);
    this.selectedHighlight.position.set(coords.x, 0.41, coords.z);
    this.selectedHighlight.visible = true;
  }

  public setCheckSquare(square: Square | null): void {
    if (!square) {
      this.checkHighlight.visible = false;
      return;
    }
    const coords = squareToCoords(square);
    this.checkHighlight.position.set(coords.x, 0.412, coords.z);
    this.checkHighlight.visible = true;
  }

  public setLastMove(from: Square | null, to: Square | null): void {
    if (!from || !to) {
      this.lastMoveFromHighlight.visible = false;
      this.lastMoveToHighlight.visible = false;
      return;
    }

    const cFrom = squareToCoords(from);
    this.lastMoveFromHighlight.position.set(cFrom.x, 0.405, cFrom.z);
    this.lastMoveFromHighlight.visible = true;

    const cTo = squareToCoords(to);
    this.lastMoveToHighlight.position.set(cTo.x, 0.405, cTo.z);
    this.lastMoveToHighlight.visible = true;
  }

  public showLegalMoves(moves: { to: Square; isCapture?: boolean }[]): void {
    this.clearLegalMoves();

    moves.forEach(({ to, isCapture }) => {
      let mesh = this.highlightPool.pop();
      if (!mesh) {
        // If capture: fiery red ring; if empty: bright cyan ring
        mesh = isCapture
          ? this.createRingHighlight(0xff2244, 0.85)
          : this.createRingHighlight(0x00e5a3, 0.75);
        this.group.add(mesh);
      } else {
        const mat = mesh.material as THREE.MeshBasicMaterial;
        mat.color.setHex(isCapture ? 0xff2244 : 0x00e5a3);
        mat.opacity = isCapture ? 0.85 : 0.75;
      }

      const coords = squareToCoords(to);
      mesh.position.set(coords.x, 0.41, coords.z);
      mesh.visible = true;
      this.legalMoveHighlights.push(mesh);
    });
  }

  public clearLegalMoves(): void {
    for (const m of this.legalMoveHighlights) {
      m.visible = false;
      this.highlightPool.push(m);
    }
    this.legalMoveHighlights = [];
  }

  public hideAllHighlights(): void {
    this.selectedHighlight.visible = false;
    this.checkHighlight.visible = false;
    this.lastMoveFromHighlight.visible = false;
    this.lastMoveToHighlight.visible = false;
    this.clearLegalMoves();
  }

  public update(time: number): void {
    // Subtle mystical pulsing on check ring & selected ring
    if (this.checkHighlight.visible) {
      const pulse = 0.75 + Math.sin(time * 6) * 0.25;
      (this.checkHighlight.material as THREE.MeshBasicMaterial).opacity = pulse;
      this.checkHighlight.rotation.y = time * 0.8;
    }

    if (this.selectedHighlight.visible) {
      this.selectedHighlight.rotation.y = -time * 0.6;
    }

    if (this.legalMoveHighlights.length > 0) {
      const scale = 1.0 + Math.sin(time * 4) * 0.05;
      for (const m of this.legalMoveHighlights) {
        m.scale.set(scale, scale, scale);
      }
    }
  }
}
